import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
} from '@reduxjs/toolkit';
import { rejectValue, flatRegularProduct } from 'redux/helpers';
import { priceForAPI } from 'utils/price';

import { GET_PRODUCT_LIST, POST_PRODUCT, PATCH_PRODUCT } from 'services/api';
import { REGULAR_PRODUCT } from 'services/api/types';
import {
  PRODUCT,
  PRODUCT_ALL_ACTION,
  PRODUCT_CREATE_ACTION,
  PRODUCT_UPDATE_ACTION,
} from './types';

const productAdapter = createEntityAdapter({
  sortComparer: (a, b) => b.id.localeCompare(a.id),
});

export const initialState = productAdapter.getInitialState({
  loading: false,
  pagination: {},
});

export const getProductList = createAsyncThunk(
  PRODUCT_ALL_ACTION,
  async ({ page }, { rejectWithValue, getState }) => {
    try {
      const storeId = getState().javelinStore.current;
      const _page = page || getState().fundraiser.pagination.currentPage || 1;
      const { data, meta } = await GET_PRODUCT_LIST({
        page: _page,
        type: REGULAR_PRODUCT,
      });

      if (!data.length)
        return { data: [], pagination: meta.pagination, storeId };

      const productData = data.map((item) => {
        const clonedObj = { ...item };
        delete clonedObj.productable;

        return {
          ...clonedObj,
          page: meta.pagination.current_page || 1,
          ...flatRegularProduct(item),
        };
      });
      return { data: productData, pagination: meta.pagination, storeId };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const createProduct = createAsyncThunk(
  PRODUCT_CREATE_ACTION,
  async (payload, { rejectWithValue, getState, dispatch }) => {
    const requestBody = {
      ...payload,
      type: REGULAR_PRODUCT,
      store_id: getState().javelinStore.current,
      price: priceForAPI(payload.price),
      currency: 'EUR',
      on_sale: true,
    };

    try {
      await POST_PRODUCT(requestBody);
      dispatch(getProductList({}));
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const editProduct = createAsyncThunk(
  PRODUCT_UPDATE_ACTION,
  async (payload, { rejectWithValue, getState }) => {
    let requestBody = payload;

    if (payload.price) {
      requestBody.price = priceForAPI(payload.price);
    }

    try {
      const { data } = await PATCH_PRODUCT(requestBody);
      const clonedObj = { ...data };
      delete clonedObj.productable;

      return {
        id: data.id,
        changes: {
          ...clonedObj,
          ...flatRegularProduct(data),
        },
      };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

const productSlice = createSlice({
  name: PRODUCT,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getProductList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getProductList.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getProductList.fulfilled, (state, { payload, meta }) => {
        state.loading = false;
        if (!!payload.pagination) {
          state.pagination = {
            totalPages: payload.pagination.total_pages || 1,
            currentPage: payload.pagination.current_page || 1,
          };
        }
        productAdapter.setAll(state, payload.data);
      })
      .addCase(editProduct.fulfilled, productAdapter.updateOne)
      .addDefaultCase((state) => state);
  },
});

export const productSelectors = productAdapter.getSelectors(
  (state) => state.product
);

export default productSlice.reducer;
